import { apiRequest } from '../../../services/api/client';
import { Magazine, MagazineLesson } from '../types/trimestersSettings.types';

type MagazineLessonResource = {
  id: number;
  number: number;
  title: string;
  summary: string | null;
  biblical_reference: string | null;
};

type MagazineResource = {
  id: number;
  quarter_id: number;
  segment_id: number;
  title: string;
  description: string | null;
  active: boolean;
  lessons: MagazineLessonResource[];
};

type MagazineListResponse = {
  data: MagazineResource[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    has_more_pages: boolean;
  };
};

type MagazineResponse = {
  data: MagazineResource;
};

export type MagazineImportField = {
  key: string;
  label: string;
  required: boolean;
  description: string;
};

export type MagazineImportPreview = {
  fileName: string;
  columns: string[];
  rowsCount: number;
};

export type MagazineImportResult = {
  magazines: number;
  lessons: number;
};

type MagazineImportFieldsResponse = {
  data: MagazineImportField[];
};

type MagazineImportPreviewResponse = {
  data: {
    file_name: string;
    columns: string[];
    rows_count: number;
  };
};

type MagazineImportResponse = {
  data: MagazineImportResult;
};

const toLesson = (lesson: MagazineLessonResource): MagazineLesson => ({
  id: String(lesson.id),
  number: lesson.number,
  title: lesson.title,
  summary: lesson.summary ?? '',
  biblicalReference: lesson.biblical_reference ?? '',
});

const toMagazine = (magazine: MagazineResource): Magazine => ({
  id: String(magazine.id),
  title: magazine.title,
  trimesterId: String(magazine.quarter_id),
  segmentId: String(magazine.segment_id),
  description: magazine.description ?? '',
  lessons: [...(magazine.lessons ?? [])].sort((a, b) => a.number - b.number).map(toLesson),
});

const toPayload = (magazine: Magazine) => ({
  quarter_id: Number(magazine.trimesterId),
  segment_id: Number(magazine.segmentId),
  title: magazine.title,
  description: magazine.description || null,
  active: true,
  lessons: magazine.lessons.map((lesson) => ({
    number: lesson.number,
    title: lesson.title,
    summary: lesson.summary || null,
    biblical_reference: lesson.biblicalReference || null,
  })),
});

export const magazinesService = {
  async importFields() {
    const payload = await apiRequest<MagazineImportFieldsResponse>('/ebd/magazines/import-fields');
    return payload.data;
  },

  async previewImport(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const payload = await apiRequest<MagazineImportPreviewResponse>('/ebd/magazines/import-preview', {
      method: 'POST',
      body: formData,
    });

    return {
      fileName: payload.data.file_name,
      columns: payload.data.columns,
      rowsCount: payload.data.rows_count,
    };
  },

  async importLessons(input: { file: File; trimesterId: string; mapping: Record<string, string> }) {
    const formData = new FormData();
    formData.append('file', input.file);
    formData.append('quarter_id', input.trimesterId);
    formData.append('mapping', JSON.stringify(input.mapping));

    const payload = await apiRequest<MagazineImportResponse>('/ebd/magazines/import', {
      method: 'POST',
      body: formData,
    });

    return payload.data;
  },

  async list(trimesterId?: string) {
    const endpoint = trimesterId ? `/ebd/quarters/${trimesterId}/magazines?per_page=100` : '/ebd/magazines?per_page=100';
    const firstPage = await apiRequest<MagazineListResponse>(endpoint);
    return firstPage.data.map(toMagazine);
  },

  async create(magazine: Magazine) {
    const payload = await apiRequest<MagazineResponse>('/ebd/magazines', {
      method: 'POST',
      body: JSON.stringify(toPayload(magazine)),
    });

    return toMagazine(payload.data);
  },

  async update(magazine: Magazine) {
    const payload = await apiRequest<MagazineResponse>(`/ebd/magazines/${magazine.id}`, {
      method: 'PUT',
      body: JSON.stringify(toPayload(magazine)),
    });

    return toMagazine(payload.data);
  },

  async remove(magazineId: string) {
    await apiRequest<void>(`/ebd/magazines/${magazineId}`, {
      method: 'DELETE',
    });
  },
};
